import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf, ShoppingCart } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { CartDrawer } from "@/components/CartDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const location = useLocation();
  const { itemCount } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/site-settings`)
      .then((res) => setLogoUrl(res.data?.logo_url || ""))
      .catch(() => {});
  }, []);

  const navLinks = [
    { name: "Shop", path: "/shop" },
    { name: "Blog", path: "/blog" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className="sticky top-0 z-40 bg-bone-cream/95 backdrop-blur-md border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Desktop Nav Left */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link font-sans text-sm tracking-wide transition-colors ${
                    isActive(link.path)
                      ? "text-deep-moss font-medium"
                      : "text-stone-600 hover:text-deep-moss"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Logo Center */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex items-center gap-2 group"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="WEH International"
                  data-testid="site-logo-img"
                  className="h-9 md:h-11 w-auto object-contain transition-transform group-hover:scale-105"
                />
              ) : (
                <>
                  <Leaf className="w-6 h-6 text-deep-moss transition-transform group-hover:rotate-12" />
                  <span className="font-serif text-xl md:text-2xl font-semibold text-stone-900 tracking-tight">
                    WEH International
                  </span>
                </>
              )}
            </Link>

            {/* Desktop Nav Right */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link font-sans text-sm tracking-wide transition-colors ${
                    isActive(link.path)
                      ? "text-deep-moss font-medium"
                      : "text-stone-600 hover:text-deep-moss"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {/* Cart Button */}
              <button
                onClick={() => setCartOpen(true)}
                data-testid="cart-btn"
                className="relative p-2 text-stone-600 hover:text-deep-moss transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-terracotta text-white text-xs font-sans font-medium rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
            </nav>

            {/* Mobile Right */}
            <div className="md:hidden flex items-center gap-1">
              <button
                onClick={() => setCartOpen(true)}
                data-testid="mobile-cart-btn"
                className="relative p-2 text-stone-600"
              >
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-terracotta text-white text-xs font-sans font-medium rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
              <button
                className="p-2 text-stone-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-stone-200 animate-fade-in">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`font-sans text-base py-2 transition-colors ${
                      isActive(link.path)
                        ? "text-deep-moss font-medium"
                        : "text-stone-600"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};
